import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import TeamInspection from '@/components/team/TeamInspection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Builds an authenticated, renderable photo URL from a durable object key.
const photoUrl = (key, token) => `${BACKEND_URL}/api/cleaning-inspections/file?key=${encodeURIComponent(key)}&auth=${token}`;

const normalize = (doc, token) => ({
  ...doc,
  answers: Object.fromEntries(Object.entries(doc.answers || {}).map(([k, a]) => [k, {
    rating: a.rating ?? null, exclusion: a.exclusion ?? null, note: a.note || '',
    photos: (a.photos || []).map((p) => ({ id: p.id, key: p.key, name: p.name, url: p.key ? photoUrl(p.key, token) : (p.url || p.data) })),
  }])),
});

// Strip display/transient photo fields; persist only durable references.
const strip = (doc) => ({
  id: doc.id, location_id: doc.location_id, area_index: doc.area_index || 0,
  answers: Object.fromEntries(Object.entries(doc.answers || {}).map(([k, a]) => [k, {
    rating: a.rating ?? null, exclusion: a.exclusion ?? null, note: a.note || '',
    photos: (a.photos || []).filter((p) => p.key).map((p) => ({ id: p.id, key: p.key, name: p.name })),
  }])),
});

export default function TeamInspectionsPanel({ onBack }) {
  const { token, user } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [assignments, setAssignments] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/team/inspections/assignments`, auth)
      .then((r) => setAssignments(r.data))
      .catch((e) => {
        if (e?.response?.status === 403) setError('not-authorized');
        else setError('load-failed');
      });
  }, []);

  const store = {
    list: async () => (await axios.get(`${API}/team/inspections/mine`, auth)).data.map((d) => normalize(d, token)),
    load: async (id) => normalize((await axios.get(`${API}/team/inspections/${id}`, auth)).data, token),
    save: async (doc) => normalize((await axios.put(`${API}/team/inspections/draft`, strip(doc), auth)).data, token),
  };
  const uploadPhoto = async (base64) => {
    const r = await axios.post(`${API}/team/inspections/photo`, { data: base64 }, auth);
    return { id: r.data.id, key: r.data.key, url: photoUrl(r.data.key, token) };
  };
  const onSubmit = async (doc) => {
    await axios.put(`${API}/team/inspections/draft`, strip(doc), auth);
    await axios.post(`${API}/team/inspections/${doc.id}/submit`, {}, auth);
  };

  const es = (() => { try { return localStorage.getItem('portal_lang') !== 'en'; } catch { return true; } })();

  if (error === 'not-authorized') {
    return (
      <div className="min-h-[50dvh] p-6" data-testid="inspections-not-authorized">
        <p className="text-base text-slate-600">{es
          ? 'Las inspecciones no están habilitadas para esta cuenta. Se requiere permiso de Inspecciones y una ubicación asignada.'
          : 'Inspections are not enabled for this account. An Inspections permission and an assigned location are required.'}</p>
        <button type="button" onClick={onBack} className="mt-4 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold" data-testid="inspections-back-btn">{es ? 'Volver' : 'Back'}</button>
      </div>
    );
  }
  if (error === 'load-failed') {
    return (
      <div className="min-h-[50dvh] p-6" data-testid="inspections-load-error">
        <p className="text-base text-red-700">{es ? 'No se pudieron cargar sus ubicaciones. Inténtelo de nuevo.' : 'Could not load your locations. Please try again.'}</p>
        <button type="button" onClick={onBack} className="mt-4 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold">{es ? 'Volver' : 'Back'}</button>
      </div>
    );
  }
  if (assignments === null) {
    return <div className="min-h-[50dvh] p-6 text-slate-500" data-testid="inspections-loading">{es ? 'Cargando…' : 'Loading…'}</div>;
  }
  return (
    <TeamInspection
      user={{ id: user?.id, name: user?.name || user?.full_name || user?.email }}
      assignments={assignments}
      store={store}
      uploadPhoto={uploadPhoto}
      onSubmit={onSubmit}
      onBack={onBack}
      preview={false}
    />
  );
}
